import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    MouseEvent,
    WheelEvent,
} from 'react';
import { IconButton } from '../atomic/buttons';
import { Link, useNavigate, useParams } from 'react-router';
import { ErrorDisplay, NotFound } from '../Errors';
import { HttpClient } from '../../utils/HttpUilts';
import { Loading } from '../atomic/effects';
import * as pdfjsLib from 'pdfjs-dist';
import { SelectField, SelectFieldOption } from '../atomic/forms';
import { DialogBox } from '../atomic/dialog';
import { Icons } from '../atomic/icons';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
).toString();

const WAYMARK_NOTE_APP_ID = 'jp.co.jiw.WaymarkNote';

interface ProjectJsonFileModel {
    fileUUID: string;
    fileName: string;
    url: string;
    mimeType: string;
    folderId: string;
    folderName: string;
}
interface ProjectJsonFolderFileCountModel {
    folderName: string;
    folderId: string;
    fileCount: number;
}
interface ProjectJsonUploadRecordsModel {
    files: ProjectJsonFileModel[];
    folderFileCounts: ProjectJsonFolderFileCountModel[];
}
interface ProjectJsonMarkerModel {
    created: string;
    annotationType: number;
    fileUUID: string;
    markerSize: number;
    pageNumber: number;
    x: number;
    y: number;
    updatedAt: string;
    serialNumber: number;
    text: string;
    id: string;
}
interface ProjectJsonPdfFileModel {
    fileUUID: string;
    markers: ProjectJsonMarkerModel[];
}
interface ProjectJsonDataModel {
    PdfFileModel: ProjectJsonPdfFileModel[];
}
interface ProjectJsonModel {
    version: string;
    uploadRecords: ProjectJsonUploadRecordsModel;
    data: ProjectJsonDataModel;
}
export interface WaymarkNoteViewrProp {
    appId: string | null;
    folderId: string;
}

export const WaymarkNoteViewControl = (props: WaymarkNoteViewrProp) => {
    const navigate = useNavigate();
    if (props.appId != WAYMARK_NOTE_APP_ID) {
        return <></>;
    }
    return (
        <IconButton
            icon="fa-solid fa-camera-retro"
            onClick={() => {
                navigate(`/app/waymarknote/${props.folderId}`);
            }}
        >
            WaymarkNote
        </IconButton>
    );
};

interface PDFModel {
    url: string;
    name: string;
    markers: MarkerModel[];
    category: string;
}
interface MarkerModel {
    url: string;
    page: number;
    id: string;
}
interface PDFViewProp {
    pdfFiles: PDFModel[];
    setError: (err: Error) => void;
    folderId: string;
}
interface RectSize {
    width: number;
    height: number;
}
interface AnnotationModel {
    id: string;
    left: number;
    top: number;
    right: number;
    bottom: number;
}
interface PDFImage {
    dataURL: string;
    width: number;
    height: number;
}

const PDFView = ({ pdfFiles, setError, folderId }: PDFViewProp) => {
    const [pdfDocument, setPdfDocument] =
        useState<pdfjsLib.PDFDocumentProxy | null>(null);
    const [pageNum, setPageNum] = useState<number>(0);
    const [page, setPage] = useState<pdfjsLib.PDFPageProxy | null>(null);

    const [imageUrl, setImageUrl] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [pdfUrl, setPdfUrl] = useState<string>('');
    const [showPdfSelect, setShowPdfSelect] = useState<boolean>(false);
    const [offsetX, setOffsetX] = useState<number>(0);
    const [offsetY, setOffsetY] = useState<number>(0);
    const [zoom, setZoom] = useState<number>(1.0);
    const [containerRect, setContainerRect] = useState<RectSize | null>(null);
    const [pdfImage, setPdfImage] = useState<PDFImage | null>(null);
    const [canvasSize, setCanvasSize] = useState<RectSize>({
        width: 0,
        height: 0,
    });

    const pageMarkers = useRef<MarkerModel[]>([]);
    const dragged = useRef<boolean>(false);
    const dragStartX = useRef<number>(0);
    const dragStartY = useRef<number>(0);
    const dragStartOffsetX = useRef<number>(0);
    const dragStartOffsetY = useRef<number>(0);
    const task = useRef<pdfjsLib.RenderTask | null>(null);
    const scale = useRef<number>(1.0);
    const markerAnnotations = useRef<AnnotationModel[] | null>(null);

    const navigate = useNavigate();

    // container が生成された時に呼ばれるコールバック
    const containerRef = useMemo(() => {
        return (div: HTMLDivElement | null) => {
            const observer = new ResizeObserver((entries) => {
                const entry = entries[0];
                setContainerRect({
                    width: entry.contentRect.width,
                    height: entry.contentRect.height,
                });
            });

            if (div) {
                observer.observe(div);
            }
        };
    }, []);

    // 画像が変更された時やオフセットが変わった時には再描画を実施する
    useEffect(() => {
        if (!pdfImage) {
            return;
        }
        // 画面端からはみ出さないようにoffsetを調節する
        if (offsetX < canvasSize.width - pdfImage.width) {
            setOffsetX(canvasSize.width - pdfImage.width);
        } else if (offsetY < canvasSize.height - pdfImage.height) {
            setOffsetY(canvasSize.height - pdfImage.height);
        } else if (offsetX > 0) {
            setOffsetX(0);
        } else if (offsetY > 0) {
            setOffsetY(0);
        }
    }, [canvasSize, offsetX, offsetY, pdfImage]);

    // ページが変わったときはページを取得しなおす
    useEffect(() => {
        setImageUrl('');
        if (!pdfDocument || pageNum <= 0) {
            return;
        }
        pdfDocument.getPage(pageNum).then((page) => {
            // アノテーションはリセットする
            // render前にリセットしておく必要があるので、useEffectでpageの更新を捕まえるのではなく
            // setPage の前に実施するようにしておく
            markerAnnotations.current = null;
            setPage(page);
        });
    }, [pdfDocument, pageNum]);

    // コンテナサイズが変わった時や画像が更新された時などにPDFの画像サイズを再計算
    useEffect(() => {
        if (!containerRect || !page) {
            return;
        }

        const viewport = page.getViewport({ scale: 1 });
        scale.current = Math.min(
            containerRect.width / viewport.width,
            containerRect.height / viewport.height
        );
        const scaledViewport = page.getViewport({ scale: scale.current });
        const { height, width } = scaledViewport;

        const imageWidth = width * zoom;
        const imageHeight = height * zoom;

        setCanvasSize({
            width: Math.min(imageWidth, containerRect.width),
            height: Math.min(imageHeight, containerRect.height),
        });

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = imageWidth;
        tempCanvas.height = imageHeight;
        const tempContext = tempCanvas.getContext('2d');
        if (!tempContext) {
            console.error('2d context の取得に失敗しました');
            return;
        }
        const zoomedViewport = page.getViewport({
            scale: scale.current * zoom,
        });
        if (task.current) {
            // 過去のタスクが動いている場合は止める
            task.current.cancel();
            task.current = null;
        }
        task.current = page.render({
            canvasContext: tempContext,
            viewport: zoomedViewport,
        });
        task.current.promise
            .then(() => {
                setPdfImage({
                    width: imageWidth,
                    height: imageHeight,
                    dataURL: tempCanvas.toDataURL(),
                });
                if (!markerAnnotations.current) {
                    page.getAnnotations()
                        .then((annotations) => {
                            markerAnnotations.current = annotations
                                .filter(
                                    ({ annotationType, titleObj }) =>
                                        annotationType == 3 &&
                                        titleObj &&
                                        'str' in titleObj
                                )
                                .map(({ titleObj, rect }) => ({
                                    id: titleObj.str,
                                    left: rect[0],
                                    top: rect[1],
                                    right: rect[2],
                                    bottom: rect[3],
                                }));
                        })
                        .catch((err) => {
                            console.error(err);
                        });
                }
            })
            .catch((err) => {
                console.error(err);
            });
    }, [containerRect, page, zoom]);

    useEffect(() => {
        if (!pdfFiles?.length) {
            return;
        }
        setPdfUrl(pdfFiles[0].url);
        pageMarkers.current = pdfFiles[0].markers;
    }, [pdfFiles]);

    // PDFのURLが変わったらPDFファイルを読込みなおす
    useEffect(() => {
        if (!pdfUrl) {
            return;
        }
        const mapUrl = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/cmaps/`;
        pdfjsLib
            .getDocument({
                url: pdfUrl,
                verbosity: 5,
                cMapUrl: mapUrl,
                cMapPacked: true,
                stopAtErrors: true,
            })
            .promise.then((pdf: pdfjsLib.PDFDocumentProxy) => {
                setPdfDocument(pdf);
                setPageNum(1);
            })
            .catch((err) => {
                console.error(err);
                setError(err);
            });
    }, [pdfUrl]);

    const onChangePageSelect = useCallback<(value: string) => void>(
        (value) => setPageNum(parseInt(value, 10)),
        []
    );
    const pageSelectOptions: SelectFieldOption[] = useMemo(() => {
        const retVal: SelectFieldOption[] = [];
        if (pdfDocument) {
            for (let i = 1; i <= pdfDocument.numPages; i++) {
                retVal.push({
                    value: String(i),
                    text: `page: ${i}`,
                });
            }
        }
        return retVal;
    }, [pdfDocument]);

    // 座標変換をして一致するマーカを探す
    const searchMarker = useCallback(
        (e: MouseEvent<HTMLDivElement>) => {
            if (!page) {
                return null;
            }
            // PDFのページ上の座標を取得
            const { left, top, right, bottom } = (
                e.target as HTMLDivElement
            ).getBoundingClientRect();

            const pdf_width = page.view[2];
            const pdf_height = page.view[3];
            const [x, y] = ((x, y, rotate) => {
                if (rotate == 90) {
                    return [y, x];
                } else if (rotate == 180) {
                    return [pdf_width - x, y];
                } else if (rotate == 270) {
                    return [pdf_width - y, pdf_height - x];
                } else if (rotate != 0) {
                    // rotate が 0, 90, 180, 270 以外の場合はログにエラーを吐いておく
                    console.error(`invalid rotate: ${page.rotate}`);
                }
                return [x, pdf_height - y];
            })(
                (e.clientX - left - offsetX) / zoom / scale.current,
                (e.clientY - top - offsetY) / zoom / scale.current,
                page.rotate
            );

            // 当該ページのマーカのマップを作成しておく
            const marker_map: {
                [key: string]: MarkerModel;
            } = {};
            pageMarkers.current
                .filter(({ page }) => page == pageNum)
                .forEach((marker) => {
                    marker_map[marker.id] = marker;
                });

            // マーカが重なっている場合は上のマーカに先に反応するように
            // PDFページから取得したアノテーションを逆順にしてから当たり判定を実施する
            if (!markerAnnotations.current) {
                return null;
            }
            const annotation = markerAnnotations.current
                .filter(({ id }) => id in marker_map)
                .reverse()
                .find(({ left, top, right, bottom }) => {
                    return x >= left && x <= right && y >= top && y <= bottom;
                });
            if (!annotation) {
                return null;
            }
            return annotation.id in marker_map
                ? marker_map[annotation.id]
                : null;
        },
        [
            page,
            markerAnnotations.current,
            scale.current,
            pageMarkers.current,
            offsetX,
            offsetY,
            zoom,
        ]
    );

    const onCanvasClicked = useCallback(
        (e: MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            e.preventDefault();
            const marker = searchMarker(e);
            if (marker?.url) {
                setIsLoading(true);
                setImageUrl(marker.url);
            }
        },
        [searchMarker]
    );

    const onCanvasMouseMove = useCallback(
        (e: MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            e.preventDefault();
            if (dragged.current) {
                setOffsetX(
                    dragStartOffsetX.current + e.clientX - dragStartX.current
                );
                setOffsetY(
                    dragStartOffsetY.current + e.clientY - dragStartY.current
                );
            }
            const marker = searchMarker(e);
            (e.target as HTMLDivElement).style.cursor = (() => {
                if (marker) {
                    return marker.url ? 'pointer' : 'not-allowed';
                } else if (dragged.current) {
                    return 'grab';
                }
                return 'default';
            })();
        },
        [
            searchMarker,
            dragged,
            dragStartX,
            dragStartY,
            dragStartOffsetX,
            dragStartOffsetY,
        ]
    );

    const onCanvasMouseDown = useCallback(
        (e: MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            e.preventDefault();
            dragged.current = true;
            dragStartX.current = e.clientX;
            dragStartY.current = e.clientY;
            dragStartOffsetX.current = offsetX;
            dragStartOffsetY.current = offsetY;
        },
        [offsetX, offsetY]
    );
    const onCanvasMouseUp = useCallback((e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        dragged.current = false;
    }, []);

    const onCanvasWheel = useCallback(
        (e: WheelEvent<HTMLDivElement>) => {
            e.stopPropagation();
            e.preventDefault();
            if (!pdfDocument) {
                return;
            }
            if (e.deltaY < 0) {
                setPageNum((prevState) => {
                    if (prevState > 1) {
                        return prevState - 1;
                    }
                    return prevState;
                });
            } else if (e.deltaY > 0) {
                setPageNum((prevState) => {
                    if (prevState < pdfDocument.numPages) {
                        return prevState + 1;
                    }
                    return prevState;
                });
            }
        },
        [pdfDocument]
    );

    const onClickBeforePage = useCallback(() => {
        setPageNum((prevState) => {
            if (prevState > 1) {
                return prevState - 1;
            }
            return prevState;
        });
    }, []);

    const onClickNextPage = useCallback(() => {
        if (!pdfDocument) {
            return;
        }
        setPageNum((prevState) => {
            if (prevState < pdfDocument.numPages) {
                return prevState + 1;
            }
            return prevState;
        });
    }, [pdfDocument]);

    const onClickFirstPage = useCallback(() => {
        setPageNum(1);
    }, []);
    const onClickLastPage = useCallback(() => {
        if (!pdfDocument) {
            return;
        }
        setPageNum(pdfDocument.numPages);
    }, [pdfDocument]);
    const onImageLoaded = useCallback(() => {
        setIsLoading(false);
    }, []);
    const onOpenPdfSelect = useCallback(() => {
        setShowPdfSelect(true);
    }, []);
    const onClosePdfSelect = useCallback(() => {
        setShowPdfSelect(false);
    }, []);
    const onPDFZoomOut = useCallback(() => {
        setZoom((zoom) => {
            zoom -= 0.1;
            return zoom < 1.0 ? 1.0 : zoom;
        });
    }, []);
    const onPDFZoomIn = useCallback(() => {
        setZoom((zoom) => {
            zoom += 0.1;
            return zoom > 2.0 ? 2.0 : zoom;
        });
    }, []);

    if (!pdfDocument) {
        return <Loading />;
    }
    return (
        <div className="app-main app-waymarknote">
            <div className="pdfview-container">
                <div id="pdfview-header">
                    <div className="button-group">
                        <IconButton
                            icon={Icons.Folder}
                            onClick={() =>
                                navigate(`/app/folderlist/${folderId}`)
                            }
                        >
                            フォルダページに戻る
                        </IconButton>
                        {pdfFiles?.length > 0 && (
                            <IconButton
                                icon="fa-solid fa-book"
                                onClick={onOpenPdfSelect}
                            >
                                PDFを選択する
                            </IconButton>
                        )}
                    </div>
                    <div className="button-group">
                        <IconButton
                            icon="fa-solid fa-angles-left"
                            onClick={onClickFirstPage}
                            disabled={pageNum <= 1}
                        >
                            最初のページ
                        </IconButton>
                        <IconButton
                            icon="fa-solid fa-angle-left"
                            onClick={onClickBeforePage}
                            disabled={pageNum <= 1}
                        >
                            前ページ
                        </IconButton>
                        <SelectField
                            value={String(pageNum)}
                            _onChange={onChangePageSelect}
                            options={pageSelectOptions}
                        ></SelectField>
                        <IconButton
                            icon="fa-solid fa-angle-right"
                            onClick={onClickNextPage}
                        >
                            次ページ
                        </IconButton>
                        <IconButton
                            icon="fa-solid fa-angles-right"
                            onClick={onClickLastPage}
                        >
                            最後のページ
                        </IconButton>
                    </div>
                    <div className="button-group">
                        <IconButton
                            icon="fa-solid fa-magnifying-glass-minus"
                            onClick={onPDFZoomOut}
                            disabled={zoom <= 1.0}
                        >
                            PDFの縮小
                        </IconButton>
                        <IconButton
                            icon="fa-solid fa-magnifying-glass-plus"
                            onClick={onPDFZoomIn}
                            disabled={zoom >= 2.0}
                        >
                            PDFの拡大
                        </IconButton>
                    </div>
                </div>
                <div id="pdfview-main">
                    <div id="canvas-container" ref={containerRef}>
                        <div
                            className="canvas"
                            onClick={onCanvasClicked}
                            onWheel={onCanvasWheel}
                            onMouseMove={onCanvasMouseMove}
                            onMouseDown={onCanvasMouseDown}
                            onMouseUp={onCanvasMouseUp}
                            onMouseOut={onCanvasMouseUp}
                            style={{
                                width: `${canvasSize.width}px`,
                                height: `${canvasSize.height}px`,
                                backgroundImage: pdfImage?.dataURL
                                    ? `url(${pdfImage?.dataURL})`
                                    : '',
                                backgroundPosition: `${offsetX}px ${offsetY}px`,
                            }}
                        />
                    </div>
                </div>
            </div>
            {!!imageUrl && (
                <div
                    className="atomic dialog-backdrop"
                    onClick={() => setImageUrl('')}
                >
                    {isLoading && <Loading />}
                    <img
                        src={imageUrl}
                        className="marker-image"
                        onLoad={onImageLoaded}
                    />
                </div>
            )}
            {showPdfSelect && (
                <DialogBox
                    title="PDFファイル一覧"
                    disabledCancel={true}
                    okText="閉じる"
                    onOK={onClosePdfSelect}
                >
                    <table className="atomic table project-list">
                        <thead>
                            <tr>
                                <th>ファイル名</th>
                                <th>区分</th>
                                <th>マーカ数</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pdfFiles.map(
                                ({ name, url, markers, category }, i) => (
                                    <tr key={i}>
                                        <td>
                                            {url == pdfUrl ? (
                                                name
                                            ) : (
                                                <Link
                                                    to="#"
                                                    onClick={() => {
                                                        setPdfUrl(url);
                                                        setShowPdfSelect(false);
                                                    }}
                                                >
                                                    {name}
                                                </Link>
                                            )}
                                        </td>
                                        <td>
                                            {category == 'zumen'
                                                ? '野帳'
                                                : '過年度写真帳'}
                                        </td>
                                        <td>{markers?.length ?? 0}</td>
                                    </tr>
                                )
                            )}
                        </tbody>
                    </table>
                </DialogBox>
            )}
        </div>
    );
};

interface ProjectProps {
    folderId: string;
    setError: (err: Error) => void;
}

const Project = ({ folderId, setError }: ProjectProps) => {
    const [pdfFiles, setPdfFiles] = useState<PDFModel[] | null>(null);

    useEffect(() => {
        if (!folderId) {
            return;
        }
        HttpClient.get(`/item/${folderId}`)
            .then((resp) => {
                if (!resp?.data?.folder) {
                    throw new Error('フォルダ情報の取得に失敗しました');
                }
                if (resp?.data?.folder?.appId != WAYMARK_NOTE_APP_ID) {
                    throw new Error('WaymarkNoteのフォルダではありません');
                }
                // プロジェクトフォルダ直下に100個以上ファイルが存在しているとエラーになる可能性があるが
                // 基本的に大丈夫のはず
                HttpClient.get(
                    `/items/${folderId}?folder=0&file=1&limit=100&offset=0`
                )
                    .then((resp) => {
                        if (!resp?.data) {
                            throw new Error('ファイルの取得に失敗しました');
                        }
                        const len = resp.data?.length;
                        if (!len) {
                            throw new Error('ファイルの取得に失敗しました');
                        }
                        let project_file_exists = false;
                        for (let i = 0; i < len; i++) {
                            if (resp.data[i].name == 'project.json') {
                                project_file_exists = true;
                                fetch(resp.data[i].url)
                                    .then((resp) => {
                                        resp?.json()
                                            .then(
                                                (
                                                    projectJson: ProjectJsonModel
                                                ) => {
                                                    // Project.json のバージョンによって図面フォルダ名を変える
                                                    // issue936
                                                    const [
                                                        zumen_folder_name,
                                                        report_folder_name,
                                                    ] = (() => {
                                                        if (
                                                            projectJson?.version ==
                                                            1
                                                        ) {
                                                            return [
                                                                'zumen',
                                                                'report',
                                                            ];
                                                        } else if (
                                                            projectJson?.version ==
                                                            2
                                                        ) {
                                                            return [
                                                                'zumen_export',
                                                                'report_export',
                                                            ];
                                                        }
                                                        throw Error(
                                                            `invalid project version: ${projectJson?.version}`
                                                        );
                                                    })();

                                                    // 対象となる図面フォルダが存在しない場合はエラー
                                                    // issue936
                                                    if (
                                                        !projectJson?.uploadRecords?.folderFileCounts?.find(
                                                            ({ folderName }) =>
                                                                folderName?.toLowerCase() ==
                                                                zumen_folder_name
                                                        )
                                                    ) {
                                                        throw Error(
                                                            `${zumen_folder_name} フォルダが存在しません`
                                                        );
                                                    }

                                                    const files: {
                                                        [
                                                            key: string
                                                        ]: ProjectJsonFileModel;
                                                    } = {};
                                                    const data =
                                                        projectJson?.data;
                                                    if (!data) {
                                                        throw Error(
                                                            'invalid project.json'
                                                        );
                                                    }
                                                    // ファイル情報のマップを作成
                                                    projectJson?.uploadRecords?.files?.forEach(
                                                        (file) => {
                                                            files[
                                                                file.fileUUID
                                                            ] = file;
                                                        }
                                                    );
                                                    if (
                                                        Object.keys(files)
                                                            .length === 0
                                                    ) {
                                                        throw Error(
                                                            'file not found'
                                                        );
                                                    }
                                                    const pdfFiles =
                                                        data?.PdfFileModel?.map(
                                                            (pdf) => ({
                                                                name: files[
                                                                    pdf.fileUUID
                                                                ].fileName,
                                                                url: files[
                                                                    pdf.fileUUID
                                                                ].url,
                                                                markers:
                                                                    pdf.markers
                                                                        .filter(
                                                                            ({
                                                                                annotationType,
                                                                            }) =>
                                                                                annotationType ==
                                                                                0
                                                                        )
                                                                        .map(
                                                                            (
                                                                                marker
                                                                            ) => ({
                                                                                url: marker.fileUUID
                                                                                    ? files[
                                                                                          marker
                                                                                              .fileUUID
                                                                                      ]
                                                                                          .url
                                                                                    : null,
                                                                                page: marker.pageNumber,
                                                                                id: marker.id,
                                                                            })
                                                                        ),
                                                                category: ((
                                                                    folder_name: string
                                                                ) => {
                                                                    if (
                                                                        folder_name ==
                                                                        zumen_folder_name
                                                                    ) {
                                                                        return 'zumen';
                                                                    }
                                                                    if (
                                                                        folder_name ==
                                                                        report_folder_name
                                                                    ) {
                                                                        return 'report';
                                                                    }
                                                                    return 'unknown';
                                                                })(
                                                                    files[
                                                                        pdf
                                                                            .fileUUID
                                                                    ].folderName?.toLowerCase()
                                                                ),
                                                            })
                                                        )?.filter(
                                                            ({ category }) =>
                                                                category ==
                                                                    'zumen' ||
                                                                category ==
                                                                    'report'
                                                        );
                                                    if (!pdfFiles?.length) {
                                                        throw Error(
                                                            '図面が見つかりませんでした'
                                                        );
                                                    }
                                                    pdfFiles.sort((a, b) => {
                                                        // 図面と調書では図面の方が先
                                                        if (
                                                            a.category ==
                                                                'zumen' &&
                                                            b.category ==
                                                                'report'
                                                        ) {
                                                            return -1;
                                                        }
                                                        if (
                                                            a.category ==
                                                                'report' &&
                                                            b.category ==
                                                                'zumen'
                                                        ) {
                                                            return 1;
                                                        }
                                                        // 同じカテゴリなら名前順
                                                        return a.name > b.name
                                                            ? 1
                                                            : -1;
                                                    });
                                                    setPdfFiles(pdfFiles);
                                                }
                                            )
                                            .catch((err) => {
                                                console.error(err);
                                                setError(err);
                                            });
                                    })
                                    .catch((err) => {
                                        console.error(err);
                                        setError(err);
                                    });
                            }
                        }
                        if (!project_file_exists) {
                            throw Error('project.json が見つかりませんでした');
                        }
                    })
                    .catch((err) => {
                        console.error(err);
                        setError(err);
                    });
            })
            .catch((err) => {
                console.error(err);
                setError(err);
            });
    }, [folderId]);

    if (!pdfFiles) {
        return <Loading />;
    }

    return (
        <PDFView pdfFiles={pdfFiles} setError={setError} folderId={folderId} />
    );
};

export const WaymarkNoteView = () => {
    const { folderId } = useParams();
    const [error, setError] = useState<Error | null>(null);

    if (!folderId) {
        return <NotFound />;
    }

    if (error) {
        return <ErrorDisplay error={error} />;
    }
    return <Project folderId={folderId} setError={setError} />;
};
